import styled from 'styled-components';
import {
  ParagraphLargeText,
  ParagraphSmallText,
} from '../Texts';
import Button from '../Button';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import TextInput, { Label } from '../Inputs/Text';
import VerificationCodeInput from '../VerificationCodeInput';
import { useEffect, useRef, useState } from 'react';
import {
  useSendOtpCodeMutation,
  useVerifyOtpCodeMutation,
} from '../../redux/api/authorized/phoneVerification';
import { toast } from 'react-toastify';
import UsIcon from '../../assets/icons/us.svg?react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { formatPhoneNumber, unformatPhoneNumber } from '../../common/phone';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';

const VerificationCodeInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const CountryCodeContainer = styled.div`
  border: unset;
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  padding-left: 16px;
  padding-right: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: ${({ theme }) => theme.text.regular};
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: normal;
  white-space: nowrap;
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const phoneNumberSchema = yup.object({
  phoneNumber: yup.string().required('Phone number is required'),
  otpCode: yup.string().when('codeSent', {
    is: true,
    then: (schema) => schema.required('OTP code is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  codeSent: yup.boolean().default(false),
});

const PhoneVerificationForm = ({ onVerified, onBack, showTitle = true, containerStyle = {} }) => {
  const otpRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fullyRendered, setFullyRendered] = useState(false);
  const inputRef = useRef(null);

  const [sendOtpCode] = useSendOtpCodeMutation();
  const [verifyOtpCode] = useVerifyOtpCodeMutation();
  const { refetch: refetchCurrentUser } = useGetCurrentUserProfileQuery();

  useEffect(() => {
    if (!fullyRendered) return;
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [fullyRendered, inputRef.current]);

  useEffect(() => {
    setFullyRendered(true);
  }, []);

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(phoneNumberSchema),
    defaultValues: {
      phoneNumber: '',
      otpCode: '',
      codeSent: false,
    },
  });

  const phoneNumber = watch('phoneNumber');
  const watchedCodeSent = watch('codeSent');

  const handleVerifyOTPCode = async (code) => {
    setLoading(true);
    try {
      const response = await verifyOtpCode({
        code,
        phoneNumber: `+1${phoneNumber}`,
      });
      if (response.error) {
        toast.error('Invalid code');
        otpRef.current.clear();
      } else {
        if (
          Capacitor.isNativePlatform() &&
          Capacitor.isPluginAvailable('Keyboard')
        ) {
          await Keyboard.hide();
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        await refetchCurrentUser();
        onVerified();
      }
    } catch (error) {
      otpRef.current.clear();
      toast.error('Invalid code');
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: '100%',
        flex: 1,
        ...containerStyle,
      }}
    >
      {!watchedCodeSent && (
        <>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              textAlign: 'left',
              gap: '4px',
            }}
          >
            <ParagraphLargeText>Enter your mobile number</ParagraphLargeText>
            <ParagraphSmallText>
              Verify your mobile number to continue with Apple Pay using a
              debit card.
            </ParagraphSmallText>
          </div>
          <Controller
            name='phoneNumber'
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <Label>Phone Number</Label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <CountryCodeContainer>
                    <UsIcon width={20} height={20} />
                    <p>US (+1)</p>
                  </CountryCodeContainer>
                  <TextInput
                    ref={inputRef}
                    inputMode='numeric'
                    placeholder='(111) 111-1111'
                    value={formatPhoneNumber(value)}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      const unformatted = unformatPhoneNumber(formatted);
                      onChange(unformatted);
                    }}
                    onBlur={onBlur}
                    error={errors.phoneNumber?.message}
                    disabled={watchedCodeSent}
                  />
                </div>
              </div>
            )}
          />
        </>
      )}
      {watchedCodeSent && (
        <>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              textAlign: 'left',
              gap: '4px',
            }}
          >
            <ParagraphLargeText>Verify your mobile number</ParagraphLargeText>
            <ParagraphSmallText>
              Enter the 6-digit code sent by text to{' '}
              {`+1 ${formatPhoneNumber(phoneNumber)}`}
            </ParagraphSmallText>
          </div>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Label>Verification Code</Label>
            <VerificationCodeInputContainer>
              <VerificationCodeInput
                ref={otpRef}
                length={6}
                loading={loading}
                onComplete={(code) => handleVerifyOTPCode(code)}
                size='md'
                autoFocus
              />
            </VerificationCodeInputContainer>
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
        {onBack && (
          <Button
            variant='white'
            onClick={onBack}
            style={{ flex: 1 }}
          >
            BACK
          </Button>
        )}
        {!watchedCodeSent && (
          <Button
            fullWidth={!onBack}
            style={{ flex: onBack ? 1 : 'unset' }}
            loading={loading}
            onClick={async () => {
              setLoading(true);
              try {
                const response = await sendOtpCode({
                  phoneNumber: `+1${phoneNumber}`,
                });
                if (response.error) {
                  toast.error('Invalid phone number');
                } else {
                  setValue('codeSent', true);
                }
              } catch (error) {
                toast.error('Invalid phone number');
              }
              setLoading(false);
            }}
          >
            Send Code
          </Button>
        )}
      </div>
    </div>
  );
};

export default PhoneVerificationForm;
